import { useTranslation } from "react-i18next";

import { Container } from "../components/ui";
import { useLocale } from "../i18n/useLocale";
import { getLegalDocument, type LegalKind } from "../content/legal";

/**
 * The terms and the privacy policy.
 *
 * One screen for both — they differ only in which document is fetched, and
 * splitting them would mean maintaining the same page twice.
 *
 * The body is HTML the CMS already sanitized on save (`nh3` allow-list,
 * server-side): that is the XSS boundary, and the same trust the blog places in
 * article HTML. Rendering happens through the typography plugin so a legal
 * document written as headings and clauses reads like one.
 */
export default function Legal({ kind }: { kind: LegalKind }) {
  const { t } = useTranslation("legal");
  const { locale } = useLocale();
  const doc = getLegalDocument(locale, kind);

  if (!doc) {
    return (
      <Container className="py-24">
        <h1 className="text-3xl font-semibold">{t(`${kind}.title`)}</h1>
        <p className="mt-4 text-slate-600">{t("unavailable")}</p>
        <a className="mt-2 inline-block underline" href="mailto:info@done-it.app">
          info@done-it.app
        </a>
      </Container>
    );
  }

  // The API serves the source document whole when a locale has not been
  // translated. Saying so is the honest thing: a reader on the Polish site
  // should know why the page is in English, not wonder whether it is a bug.
  const untranslated = doc.language !== locale;

  return (
    <Container className="py-16 md:py-24">
      <article className="mx-auto max-w-3xl">
        <h1 className="text-3xl font-semibold md:text-4xl">{doc.title || t(`${kind}.title`)}</h1>

        <p className="mt-3 text-sm text-slate-500">
          {doc.version && t("version", { version: doc.version })}
          {doc.effective_from && ` · ${t("effectiveFrom", { date: doc.effective_from })}`}
        </p>

        {untranslated && (
          <p className="mt-6 rounded-lg bg-slate-50 px-4 py-3 text-sm text-slate-600">
            {t("onlyInLanguage", { language: doc.language.toUpperCase() })}
          </p>
        )}

        <div
          className="prose prose-slate mt-10 max-w-none prose-headings:font-semibold prose-a:text-blue-600"
          // Sanitized server-side on save — see the note above.
          dangerouslySetInnerHTML={{ __html: doc.body }}
        />
      </article>
    </Container>
  );
}
